import { Button, Input, message, Space, Spin, Table } from "antd";
import { CloseOutlined, CopyOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { serverFetch } from "@server";
import { fmtDate } from "@extensions/convert";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "./order.css";

const buildClientLink = (order) => {
  if (!order?.id) return "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const guid = (order.orderGuid || "").replace(/\{|\}|-/g, "").toLowerCase();
  return `${origin}/doc/${guid}-${order.id}`;
};

const Order = ({ orderId, code, onClose, standalone = false }) => {
  const viewport = useViewportBreakpoint();
  const isMobile = viewport === "mobile";
  /** Модалка со списка заказов: нижний sheet как у CompanyBase */
  const sheetMobile = isMobile && !standalone;
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewImgUrl, setPreviewImgUrl] = useState(null);

  useEffect(() => {
    if (code) {
      setLoading(true);
      setOrder(null);
      serverFetch(`doc/${encodeURIComponent(code)}`)
        .then((data) => {
          setOrder(data);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          if (e?.status === 404) message.error("Документ не найден");
          else message.error(e?.userMessage || e?.message || "Ошибка загрузки документа");
          onClose?.();
        });
    } else if (orderId) {
      setLoading(true);
      setOrder(null);
      serverFetch(`Order/${orderId}`)
        .then((data) => {
          setOrder(data);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          if (e?.status === 404) message.error("Заказ не найден");
          else if (e?.status === 403) message.error("Нет доступа к заказу");
          else message.error(e?.userMessage || e?.message || "Ошибка загрузки заказа");
          onClose?.();
        });
    }
  }, [orderId, code]);

  if (!orderId && !code) return null;

  const copyClientLink = () => {
    const url = order ? buildClientLink(order) : "";
    if (url && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => message.success("Ссылка скопирована в буфер обмена"));
    }
  };

  const formatSum = (v) => (v != null && v !== "" ? Number(v).toLocaleString("ru-RU", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : "—");

  const itemColumns = [
    {
      title: "Фото",
      render: (_, record) => {
        const url = record.img;
        const no = record.itemNo ?? "—";
        return (
          <div className="order-item-photo-wrap">
            <span className="order-item-no-badge">{no}</span>
            {url ? (
              <img
                src={url}
                className="order-item-photo"
                alt=""
                onClick={() => setPreviewImgUrl(url)}
              />
            ) : (
              <span className="order-item-photo-empty">—</span>
            )}
          </div>
        );
      },
      onCell: () => ({ className: "order-table-cell-top" }),
    },
    {
      title: "Позиция",
      width: 130,
      render: (_, record) => {
        const { name, w, h, qty, syma } = record;
        const nameTrim = (name ?? "").trim();
        const hasW = w != null && w !== "";
        const hasH = h != null && h !== "";
        const dimLabel = hasW && hasH ? "Габарит" : hasW ? "Длина" : null;
        const dimVal = hasW && hasH ? `${w} x ${h}` : hasW ? String(w) : null;
        const rows = [
          ...(nameTrim ? [["Наименование", nameTrim]] : []),
          ...(dimLabel ? [[dimLabel, dimVal]] : []),
          ["Количество", qty != null && qty !== "" ? qty : "—"],
          ["Стоимость", formatSum(syma)],
        ];
        return (
          <div className="order-item-position-stack">
            {rows.map(([label, value], i) => (
              <div key={i} className="order-item-position-field">
                <span className="order-item-position-label">{label}</span>
                <span className="order-item-position-value">{value}</span>
              </div>
            ))}
          </div>
        );
      },
      onCell: () => ({ className: "order-table-cell-top" }),
    },
  ];

  const orderFieldsAndItems = !loading && order && (
    <>
      <div className="order-fields-grid">
        {[
          ["Дата документа", fmtDate(order.docDate)],
          ["Номер документа", order.docNum ?? "—"],
          ["Статус", order.status ?? "—"],
          ["Отдел", order.depNo ?? "—"],
          ["Клиент", order.predName ?? "—"],
          ["Адрес", order.address ?? "—"],
          ["Телефон", order.phone ?? "—"],
          ["Дата готовности", fmtDate(order.finDate)],
          ["Площадь", order.area != null ? Number(order.area) : "—"],
          ["Сумма", order.syma != null ? Number(order.syma) : "—"],
          ["Долг", order.debet != null ? Number(order.debet) : "—"],
        ].map(([label, value], i) => (
          <div key={i} className="order-field-cell">
            <span className="order-field-label">{label}</span>
            <span className="order-field-value">{value}</span>
          </div>
        ))}
      </div>
      {order.items && order.items.length > 0 && (
        <Table
          className="order-items-table"
          dataSource={order.items}
          columns={itemColumns}
          rowKey="id"
          pagination={false}
          size="small"
          tableLayout="fixed"
        />
      )}
    </>
  );

  const panelClassName = [
    "misoft-modal-content",
    sheetMobile && "company-base-sheet--mobile",
    !sheetMobile && "order-panel",
    standalone && "order-panel--standalone",
  ]
    .filter(Boolean)
    .join(" ");

  const panel = (
    <div
      className={panelClassName}
      onClick={standalone ? undefined : (e) => e.stopPropagation()}
    >
      {sheetMobile ? (
        <>
          <div className="company-base-sheet-header">
            <span className="company-base-sheet-title">{order ? `Заказ #${order.docNum ?? order.id}` : "Заказ"}</span>
            <Space size="small" className="company-base-sheet-actions">
              <Button
                type="default"
                icon={<CopyOutlined />}
                onClick={copyClientLink}
                disabled={!order}
                aria-label="Копировать ссылку"
                className="company-base-header-icon-btn"
              />
              {onClose && (
                <Button
                  type="default"
                  icon={<CloseOutlined />}
                  onClick={onClose}
                  aria-label="Закрыть"
                  className="company-base-header-icon-btn"
                />
              )}
            </Space>
          </div>
          <div className="company-base-sheet-body">
            {order && <div className="order-company-row order-company-row--sheet">{order.companyName ?? "—"}</div>}
            {loading && (
              <div className="order-loading">
                <Spin />
              </div>
            )}
            {orderFieldsAndItems}
          </div>
        </>
      ) : (
        <>
          <div className="misoft-modal-title order-title">
            <span className="order-title-text">{order ? `Заказ #${order.docNum ?? order.id}` : "Заказ"}</span>
            {!standalone && (
              <div className="order-link-row">
                <Input
                  readOnly
                  value={order ? buildClientLink(order) : ""}
                  title="Ссылка для доступа к документу без авторизации"
                  className="order-link-input"
                  size="small"
                />
                <CopyOutlined className="order-copy-icon" onClick={copyClientLink} />
              </div>
            )}
            {onClose && (
              <CloseOutlined className="misoft-section-title-close order-close-icon" onClick={onClose} />
            )}
          </div>
          {order && <div className="order-company-row">{order.companyName ?? "—"}</div>}
          <div className="order-content">
            {loading && (
              <div className="order-loading">
                <Spin />
              </div>
            )}
            {orderFieldsAndItems}
          </div>
        </>
      )}
    </div>
  );

  const previewOverlay = previewImgUrl && (
    <div
      role="button"
      tabIndex={0}
      className="order-preview-overlay"
      onClick={() => setPreviewImgUrl(null)}
      onKeyDown={(e) => e.key === "Escape" && setPreviewImgUrl(null)}
    >
      <img
        src={previewImgUrl}
        alt=""
        className="order-preview-img"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );

  if (standalone) {
    return (
      <div className="order-standalone-wrap">
        <div className="order-standalone-inner">{panel}</div>
        {previewOverlay}
      </div>
    );
  }

  return (
    <div
      className={`misoft-modal-overlay order-overlay${sheetMobile ? " company-base-overlay--mobile" : ""}`}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      {panel}
      {previewOverlay}
    </div>
  );
};

export default Order;
